import { v4 as uuidv4 } from 'uuid';

const defaultState = {
    products: [],
    items: [],
    users: [],
    addons: [],
    currentProductUuid: null,
    simOption: 0,
    owner: {},
    currentCalculation: {},
    createOrderResponse: {},
    hasControl: false,
    promoCode: null,
    validationFieldValue: null,
    permissions: [],
    currentItem: null,
    assortments: {},
    startDate: '',
    paymentStatus: -1,
    ownerRequireAddress: true,
    selectedEProvider: null,
};

const state = defaultState;

const mutations = {
    resetState: state => Object.assign(state, defaultState),
    addProduct: (state, product) => state.products.push(product),
    setPermissions: (state, permissions) => state.permissions = permissions,
    setPromoCode: (state, code) => state.promoCode = code,
    setValidationFieldValue: (state, value) => state.validationFieldValue = value,
    addAddons: (state, addons) => {
        const indexToUpdate = state.addons.findIndex(addon => addon.itemId === addons.itemId);

        if (indexToUpdate === -1) state.addons.push(addons);
        else state.addons[indexToUpdate] = addons;
    },
    setCreateOrderResponse: (state, response) => state.createOrderResponse = response,
    setSimOption: (state, type) => state.simOption = type,
    setCurrentCalculation: (state, calculation) => state.currentCalculation = calculation,
    removeUserByUuid: (state, userUuid) => {
        const userIndex = state.users.findIndex(user => user.id === userUuid);
        if (userIndex === -1) return;

        state.users.splice(userIndex, 1);
    },
    removeItemByUserUuid: (state, userUuid) => {
        const index = state.items.findIndex(item => item.userUuid === userUuid);
        if (index > -1) state.items.splice(index, 1);
    },
    removeProductByUserUuid: (state, userUuid) => {
        if (state.products.length === 0) return;

        const item = state.items.find(item => item.userUuid === userUuid);
        if (!item) return;

        const productIndex = state.products.findIndex(product => product.id === item.productUuid);
        if (productIndex === -1) return;

        state.products.splice(productIndex, 1);
    },
    removeProductByItemUuid: (state, itemUuid) => {
        if (state.products.length === 0) return;

        const item = state.items.find(item => item.uuid === itemUuid);
        if (!item) return;

        const productIndex = state.products.findIndex(product => product.uuid === item.productUuid);
        if (productIndex === -1) return;

        state.products.splice(productIndex, 1);
    },
    removeOwnerByUserUuid: (state, userUuid) => {
        if (!state.owner) return;
        if (state.owner.token) state.owner.id = '';
        else if (state.owner.id === userUuid) state.owner = undefined;

    },
    removeItemByItemUuid: (state, itemUuid) => {
        const itemIndex = state.items.findIndex(item => item.uuid === itemUuid);
        if (itemIndex === -1) return;
        state.items.splice(itemIndex, 1);
    },
    clearProducts: state => state.products = [],
    setCurrentProductUuid: (state, productUuid) => state.currentProductUuid = productUuid,
    setHasControl: (state, hasControl) => state.hasControl = hasControl,
    addUser: (state, user) => {
        const userIndex = state.users.findIndex(u => u.id == user.id);

        if (userIndex === -1) state.users.push(user);
        else {
            Object.assign(state.users[userIndex], user);
            state.users = [...state.users]; // Enforce rerender of vue state
        }
    },
    setOwner: (state, owner) => {
        const ownerIndexInUsers = state.users.findIndex(user => user.id === owner.id);

        if (ownerIndexInUsers > 0) {
            state.users.sort(user => user.id === owner.id ? -1 : 1);
            state.items.sort(item => item.userUuid === owner.id ? -1 : 1);
        }

        state.owner = owner;
    },
    setOwnerId: (state, id) => {
        state.owner.id = id;
    },
    updateProductForAllUsers: (state, product) => {
        const users = [...state.users];
        const currentItems = [...state.items];
        if (currentItems.length === 0) {
            const newItems = [];
            users.forEach(user => newItems.push({ userUuid: user.id, productUuid: product.uuid, uuid: uuidv4() }));
            state.items = [...newItems];
        } else {
            currentItems.forEach(item => item.productUuid = product.uuid);
            state.items = [...currentItems];
        }
    },
    addItem: (state, item) => state.items.push(item),
    setCurrentItem: (state, item) => state.currentItem = item,
    setAssortments: (state, assortments) => state.assortments = assortments,
    addItemForUserWithoutProduct: (state, user) => state.items.push({ userUuid: user.id, uuid: uuidv4() }),
    setStartDate: (state, startDate) => state.startDate = startDate,
    setStartDateByUserUuid: (state, startDateObject) => {
        const userIndex = state.users.findIndex(user => user.id === startDateObject.userUuid);
        if (userIndex === -1) return;
        state.users[userIndex].startDate = startDateObject.startDate;
        state.users = [...state.users]; // Enforce rerender of vue state
    },
    setIdentifierByUserUuid: (state, params) => {
        const userIndex = state.users.findIndex(user => user.id === params.userUuid);
        if (userIndex === -1 ) return;
        state.users[userIndex].identifier = params.identifier;
        state.users = [...state.users]; // Enforce rerender of vue state
    },
    setPrepaidAmountOnUser: (state, params) => {
        const userIndex = state.users.findIndex(user => user.id === params.userUuid);
        if (userIndex === -1 ) return;
        state.users[userIndex].prepaid = { amount: params.amount, autoRecharge: params.autoRecharge };
        state.users = [...state.users];
    },
    setPaymentStatus: (state, status) => {
        state.paymentStatus = status;
    },
    updateOwnerToken: (state, token) => {
        if (!state.owner) return;
        state.owner.token = token;
        state.owner = { ...state.owner }; // Enforce rerender of vue state
    },
    updateOwnerRequireAddress: (state, value) => {
        state.ownerRequireAddress = value;
    },
    setSelectedEProvider: (state, provider) => {
        state.selectedEProvider = provider;
    },
};

const actions = {
    resetState: ({ commit }) => commit('resetState'),
    setPermissions: ({ commit }, permissions) => commit('setPermissions', permissions),
    setPromoCode: ({ commit }, code) => commit('setPromoCode', code),
    setValidationFieldValue: ({ commit }, value) => commit('setValidationFieldValue', value),
    setCreateOrderResponse: ({ commit }, response) => commit('setCreateOrderResponse', response),
    setSimOption: ({ commit }, simOption) => commit('setSimOption', simOption),
    setCurrentCalculation: ({ commit }, calculation) => commit('setCurrentCalculation', calculation),
    setHasControl: ({ commit }, hasControl) => commit('setHasControl', hasControl),
    setOwner: ({ commit }, user) => commit('setOwner', user),
    setOwnerId: ({ commit }, id) => commit('setOwnerId', id),
    addProduct: ({ state, commit, rootState }, product) => {
        const maxRequiredItems = rootState.config.config.restrictions.productsMax;

        switch (rootState.flow.flow.type) {
            case 'prepaid':
            case 'single':
            case 'multi':
                commit('addProduct', { ...product });
                break;
            case 'solo':
            case 'share':
            case 'mbb':
                if (state.products.length === maxRequiredItems) commit('clearProducts');
                commit('addProduct', { ...product });
                if (state.users.length > 0) commit('updateProductForAllUsers', product);
                break;
        }
    },
    addAddons: ({ commit }, addons) => commit('addAddons', addons),
    removeUserByUuid: ({ commit }, userUuid) => commit('removeUserByUuid', userUuid),
    removeItemByUserUuid: ({ commit }, userUuid) => commit('removeItemByUserUuid', userUuid),
    removeProductByUserUuid: ({ commit }, userUuid) => commit('removeProductByUserUuid', userUuid),
    removeProductByItemUuid: ({ commit }, itemUuid) => commit('removeProductByItemUuid', itemUuid),
    removeOwnerByUserUuid: ({ commit }, userUuid) => commit('removeOwnerByUserUuid', userUuid),
    removeItemByItemUuid: ({ commit }, itemUuid) => commit('removeItemByItemUuid', itemUuid),
    addUser: ({ commit }, user) => commit('addUser', { ...user }),
    setCurrentProductUuid: ({ commit }, productUuid) => commit('setCurrentProductUuid', productUuid),
    updateProductForAllUsers: ({ commit }, product) => commit('updateProductForAllUsers', product),
    addItem: ({ commit }, item) => commit('addItem', item),
    setCurrentItem: ({ commit }, item) => commit('setCurrentItem', item),
    setAssortments: ({ commit }, assortments) => commit('setAssortments', assortments),
    addItemForUserWithoutProduct:  ({ commit }, user) => commit('addItemForUserWithoutProduct', user),
    setStartDate: ({ commit }, startDate) => commit('setStartDate', startDate),
    setStartDateByUserUuid: ({ commit }, startDateObject) => commit('setStartDateByUserUuid', startDateObject),
    setIdentifierByUserUuid: ({ commit }, params) => commit('setIdentifierByUserUuid', params),
    updateAmountOnUser: ({ commit }, params) => commit('setPrepaidAmountOnUser', params),
    setPaymentStatus: ({ commit }, status) => commit('setPaymentStatus', status),
    updateOwnerToken: ({ commit }, token) => commit('updateOwnerToken', token),
    updateOwnerRequireAddress: ({ commit }, value) => commit('updateOwnerRequireAddress', value),
    setSelectedEProvider: ({ commit }, provider) => commit('setSelectedEProvider', provider),
};

const getters = {
    allowedPortingDates: state => state.currentCalculation.allowedPortingDates,
    allowedPortingDatesUser: state => state.currentCalculation.overview.lines.find(line => line.id === state.currentItem?.uuid)?.allowedPortingDates,
    orderNumber: state => {
        const orderId = state.createOrderResponse.OrderId;
        return (orderId) ? orderId.toString() : '124124123';
    },
    isCartValid: state => {
        let isValid = true;

        state.users.forEach(user => {
            // Hacky, but a way to ensure a user has been filled in.
            if (user.firstName === '' && user.id != state.owner.id) isValid = false;
        });

        return isValid;
    },
    currentUser: state => {
        if (!state.currentItem) return undefined;
        return state.users.find(user => user.id === state.currentItem.userUuid);
    },
    cartProducts: state => {
        const cartProducts = [];

        state.items.forEach(item => {
            const obj = {};
            const user = state.users.find(user => user.id === item.userUuid);
            const product = state.products.find(product => product.uuid === item.productUuid);
            obj.uuid = item.uuid;
            obj.title = product.text.title;
            obj.user = user;
            obj.identifier = product.identifier;
            cartProducts.push(obj);
        });

        return cartProducts;
    },
    request: (state, getters, rootState) => {
        const request = {};
        request.key = rootState.config.key;
        request.pageId = rootState.flow.flow?.pages[rootState.flow.currentPageIndex].id;
        request.order = {};
        request.order.permissions = state.permissions;
        request.order.PromoCode = state.promoCode;
        request.order.validationFieldValue = state.validationFieldValue;
        request.order.owner = state.owner;
        request.order.simOption = state.simOption;
        request.order.startDate = state.startDate;
        request.order.paymentErrorCode = state.paymentStatus;
        const salesUrl = rootState.config.orderFlowConfig.endpoints.PaymentsSales;
        request.order.paymentReturnUrl = salesUrl != null ? `${salesUrl}?returnUrl=${window.location.href}` : null;
        // Unique flint situation where only a product and no users has been selected.
        if (rootState.flow.flow?.type === 'share' && state.items.length === 0 && state.products.length > 0) {
            request.order.aboUsers = [{
                addOns: null,
                uniqueId: null,
                user: null,
                productIdentifier: state.products[0].identifier,
            }];
            return request;
        }
        request.order.aboUsers = state.items.map(({ userUuid, productUuid, uuid }) => {
            const user = state.users.find(user => user.id === userUuid);
            const product = state.products.find(product => product.uuid === productUuid);
            const addons = state.addons.find(addon => addon.itemId === uuid);
            return {
                addOns: addons ? addons.addons : null,
                uniqueId: uuid,
                user: user ? user : null,
                productIdentifier: product ? product.identifier : null,
            };
        });
        return request;
    },
    currentProduct: state => state.products.find(product => product.uuid === state.currentItem.productUuid),
    getLastUserUuidOfIdentifier: state => identifier => {
        for (let index = state.users.length -1; index >= 0; index--) {
            const user = state.users[index];
            if (user.identifier.id === identifier.id) return user.id;
        }
    },
    assortments: state => state.assortments.list,
    assortmentsHash: state => state.assortments.hash,
    items: state => state.items,
    products: state => state.products,
    isAnyUserPorting: state => {
        const portingUsers = state.users.filter(user => user.isPorting);
        return portingUsers.length > 0;
    },
    identificationState: state => state.currentCalculation?.Identification?.State,
    identificationMessage: state => state.currentCalculation?.Identification?.Message,
    identifiedUser: state => state.currentCalculation?.Identification?.User,
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters,
};
